/**
 * A minimal, dependency-free ULID generator for `ol-idempotency-key` headers.
 *
 * ULID = 48-bit millisecond timestamp + 80 bits of randomness, Crockford base32 (26 chars). The
 * time field uses 10 base32 chars (50 bits) with the top 2 bits always zero, so the FIRST char is
 * naturally 0–7 until the year ~10889 — the constraint the idempotency store enforces (HANDOVER §7).
 * We additionally clamp the first char defensively.
 */
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32 (no I, L, O, U)
const TIME_LEN = 10;
const RAND_LEN = 16;

function encodeTime(now: number): string {
  let ms = now;
  let out = '';
  for (let i = TIME_LEN - 1; i >= 0; i--) {
    const mod = ms % 32;
    out = ENCODING[mod] + out;
    ms = (ms - mod) / 32;
  }
  return out;
}

function randomChar(): string {
  // crypto is available in the browser (lib DOM); fall back to Math.random only if absent.
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;
  let n: number;
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint8Array(1);
    cryptoObj.getRandomValues(buf);
    n = buf[0] % 32;
  } else {
    n = Math.floor(Math.random() * 32);
  }
  return ENCODING[n];
}

/** Generate a lexicographically-sortable ULID whose first char is guaranteed 0–7. */
export function ulid(now: number = Date.now()): string {
  let time = encodeTime(now);
  // Defensive clamp: keep the first char in 0..7 (indices 0..7 of ENCODING are '0'..'7').
  if (!'01234567'.includes(time[0])) {
    time = '0' + time.slice(1);
  }
  let rand = '';
  for (let i = 0; i < RAND_LEN; i++) rand += randomChar();
  return time + rand;
}
